import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { rrhhApi, trabajadoresApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { SearchableSelect } from '../../components/SearchableSelect';
import { FormSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import {
  Trash2, Save, Loader2, Lock, Unlock,
  Clock, UtensilsCrossed, HardHat, ClipboardCheck, AlertCircle,
  ChevronRight, PenLine, Mail, CalendarIcon, CheckSquare, Users, CheckCircle, ArrowLeft
} from 'lucide-react';

const AUSENCIA_OPTIONS = [
  { value: '', label: 'Presente', color: 'text-emerald-600 dark:text-emerald-400' },
  { value: 'F', label: 'F - Falla', color: 'text-red-600 dark:text-red-400' },
  { value: 'L', label: 'L - Licencia', color: 'text-amber-600 dark:text-amber-400' },
  { value: 'P', label: 'P - Permiso', color: 'text-blue-600 dark:text-blue-400' },
  { value: 'O', label: 'O - Otra Obra', color: 'text-purple-600 dark:text-purple-400' },
  { value: 'V', label: 'V - Vacaciones', color: 'text-cyan-600 dark:text-cyan-400' },
  { value: 'T', label: 'T - Trato', color: 'text-yellow-600 dark:text-yellow-400' },
];

function calcHours(entrada, salida) {
  if (!entrada || !salida) return 0;
  const [h1, m1] = entrada.split(':').map(Number);
  const [h2, m2] = salida.split(':').map(Number);
  return Math.max(0, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
}

// Iter 236.m.12 (code-quality fix): UID estable para asignaciones — evita el
// bug clásico de "array index as key" cuando el usuario agrega/elimina items
// del medio (React reusaba componentes con datos cruzados).
let _uidSeq = 0;
function _newUid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  _uidSeq += 1;
  return `asig-${Date.now()}-${_uidSeq}`;
}

function calcHorasFromHorario(p) {
  // Suma de tramos AM + PM
  return calcHours(p.hora_entrada, p.hora_salida) + calcHours(p.hora_entrada_2, p.hora_salida_2);
}

const DIAS_SEMANA = ['lunes','martes','miercoles','jueves','viernes','sabado','domingo'];
function getTopeDiario(fechaDmy, horarioCfg) {
  if (!fechaDmy || !horarioCfg) return 9; // fallback
  const [d, m, y] = fechaDmy.split('/').map(Number);
  const dia = DIAS_SEMANA[(new Date(y, m - 1, d).getDay() + 6) % 7]; // mon=0
  const cfg = horarioCfg[dia] || {};
  const ini = cfg.entrada;
  const fin = cfg.salida;
  if (!ini || !fin) return 0;
  const [h1, mi1] = ini.split(':').map(Number);
  const [h2, mi2] = fin.split(':').map(Number);
  const diff = (h2 * 60 + mi2) - (h1 * 60 + mi1);
  const almuerzo = parseInt(cfg.almuerzo || 0, 10);
  return Math.max(0, (diff - almuerzo) / 60);
}

function splitHnHe(totalHoras, tope) {
  const hn = Math.min(totalHoras, tope);
  const he = Math.max(0, totalHoras - hn);
  return { hn: Math.round(hn * 100) / 100, he: Math.round(he * 100) / 100 };
}

function formatDate(d) {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function parseDate(str) {
  // Iter 292.g: acepta '/' o '-' como separador para compat con datos previos.
  const parts = String(str || '').split(/[/-]/);
  if (parts.length === 3) return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  return new Date();
}

export default function ParteDiarioTerreno() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(formatDate(new Date()));
  const [calOpen, setCalOpen] = useState(false);
  const [otPrincipal, setOtPrincipal] = useState(0);
  const [otsActivas, setOtsActivas] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  // Cargado por loadRefs pero no se renderiza directamente — se mantiene por compatibilidad.
  const [_recentPartes, setRecentPartes] = useState([]);
  const [searchDate, setSearchDate] = useState('');
  const [searchOT, setSearchOT] = useState('');
  const [searchCalOpen, setSearchCalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [parte, setParte] = useState(null);
  const [personal, setPersonal] = useState([]);
  const [supervisorNombre, setSupervisorNombre] = useState('');
  const [concesionariosActivos, setConcesionariosActivos] = useState([]);
  const [prevencionistaNombre, setPrevencionistaNombre] = useState('');
  const [notaAlmuerzoExtra, setNotaAlmuerzoExtra] = useState('');
  const [lugarAlmuerzo, setLugarAlmuerzo] = useState('');
  const [lugarAlmuerzoOtro, setLugarAlmuerzoOtro] = useState('');
  const [mode, setMode] = useState('listado');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  // Feedback visual: al guardar/cerrar, el botón muestra "Grabado" verde por 2.5s
  const [savedFlash, setSavedFlash] = useState(false);
  const [closedFlash, setClosedFlash] = useState(false);
  const [horarioCfg, setHorarioCfg] = useState(null); // config global del horario para calcular HN/HE en tiempo real

  // Cargar horario configurado del ERP una vez
  useEffect(() => {
    api.get('/config/horario').then(res => setHorarioCfg(res.data)).catch(() => setHorarioCfg(null));
  }, []);
  const tope = getTopeDiario(fecha, horarioCfg);

  // Horario config
  const [horarioConfig, setHorarioConfig] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [lugarFlash, setLugarFlash] = useState(false);
  const [lastFilledIdx, setLastFilledIdx] = useState(null);
  const saveTimerRef = useRef(null);
  const handleSaveRef = useRef(null);

  const otOptions = [
    { value: 0, label: 'Sin OT (Gasto General)', prefix: '0' },
    ...otsActivas.map(ot => ({
      value: ot.numero_ot,
      label: `OT ${ot.numero_ot} - ${(ot.descripcion || '').substring(0, 40)}`,
      prefix: `${ot.numero_ot}`,
      secondary: `${ot.numero_ot} ${ot.descripcion || ''} ${ot.cliente_nombre || ''}`,
      badge: ot.proyecto_codigo || '',
    })),
  ];

  const workerOptions = allWorkers.map(w => ({
    value: w.numero,
    label: w.nombre,
    prefix: `${w.numero}`,
    secondary: `${w.nombre} ${w.cargo || ''} ${w.numero}`,
    badge: w.cargo || '',
    raw: w,
  }));

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [resOT, resW, resP, resConf, resConc] = await Promise.all([
          rrhhApi.getOTsActivas(),
          trabajadoresApi.getAll({ activo: true }),
          rrhhApi.listPartes({ limit: 10 }),
          rrhhApi.getParteConfig(),
          rrhhApi.getConcesionarios({ incluir_inactivos: false }).catch(() => ({ data: [] })),
        ]);
        setOtsActivas(resOT.data);
        setAllWorkers(resW.data);
        setRecentPartes(resP.data);
        setHorarioConfig(resConf.data);
        setConcesionariosActivos(resConc.data || []);

        // Auto-abrir parte si viene ?open={parte_id} desde el Monitor
        const params = new URLSearchParams(window.location.search);
        const openId = params.get('open');
        if (openId) {
          try {
            const r = await rrhhApi.getParte(openId);
            loadParte(r.data);
            // Limpiar el query param para evitar re-open en refresh
            navigate(window.location.pathname, { replace: true });
          } catch {
            toast({ title: 'No se pudo abrir el parte indicado', variant: 'destructive' });
          }
        }
      } catch {
        toast({ title: 'Error cargando datos', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    loadRefs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      // Usar ref para evitar closure stale (trabajadores fantasma bug).
      if (parte && parte.estado === 'abierto' && handleSaveRef.current) {
        handleSaveRef.current(true).catch(() => {});
      }
    }, 2000);
  }, [parte]);

  const handleSearchPartes = async () => {
    setSearching(true);
    try {
      const params = { limit: 20 };
      if (searchOT) params.ot_principal = parseInt(searchOT);
      const res = await rrhhApi.listPartes(params);
      let results = res.data;
      if (searchDate) {
        results = results.filter(p => p.fecha === searchDate);
      }
      setSearchResults(results);
      if (results.length === 0) toast({ title: 'No se encontraron partes para esa busqueda' });
    } catch {
      toast({ title: 'Error al buscar', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  };

  const handleCreateParte = async () => {
    if (!otPrincipal || !fecha) {
      toast({ title: 'Seleccione OT principal y fecha', variant: 'destructive' });
      return;
    }
    // Bloquea fechas del mes siguiente o posteriores
    const parts = fecha.split('/');
    if (parts.length === 3) {
      const mm = parseInt(parts[1]);
      const yyyy = parseInt(parts[2]);
      const now = new Date();
      if (yyyy > now.getFullYear() || (yyyy === now.getFullYear() && mm > (now.getMonth() + 1))) {
        toast({
          title: `Fecha ${fecha} es posterior al mes actual`,
          description: 'No se permite crear partes con fecha futura.',
          variant: 'destructive',
        });
        return;
      }
    }
    setSaving(true);
    try {
      const res = await rrhhApi.abrirParte({ fecha, ot_principal: otPrincipal });
      loadParte(res.data);
      const count = res.data.personal?.length || 0;
      const prevCount = res.data.prev_day_personal_count || 0;
      const prevFecha = res.data.prev_day_fecha || '';
      if (prevCount > count && prevFecha) {
        toast({ title: `Parte tiene ${count} personas. El ${prevFecha} tiene ${prevCount}. Use "Cargar personal día anterior" para completar.`, duration: 8000 });
      } else {
        toast({ title: count > 0 ? `Parte con ${count} trabajadores del dia anterior` : 'Parte diario creado' });
      }
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error al crear parte', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const loadParte = (data) => {
    setParte(data);
    // Auto-inicializar asignaciones vacías con el ot_principal y HH totales del horario.
    // Así al entrar a "Asignar OT y Horario" ya se ven las OTs asignadas por defecto.
    const otPrinc = data.ot_principal || 0;
    const personalConAsig = (data.personal || []).map(p => {
      if (p.ausencia || (p.asignaciones && p.asignaciones.length > 0)) {
        // Iter 236.m.12: hidratar _uid para keys estables en React (evita
        // bug de "array index as key" cuando se elimina/agrega asignaciones)
        if (p.asignaciones && p.asignaciones.length > 0) {
          p = { ...p, asignaciones: p.asignaciones.map(a => a._uid ? a : { ...a, _uid: _newUid() }) };
        }
        return p;
      }
      // Calcular HH totales del horario
      const totalHH = calcHorasFromHorario(p);
      return {
        ...p,
        asignaciones: [{ _uid: _newUid(), ot_numero: otPrinc, horas: Math.round(totalHH * 100) / 100 }],
      };
    });
    setPersonal(personalConAsig);
    setOtPrincipal(otPrinc);
    setFecha(data.fecha);
    setSupervisorNombre(data.supervisor_nombre || '');
    setPrevencionistaNombre(data.prevencionista_nombre || '');
    setNotaAlmuerzoExtra(data.nota_almuerzo_extra || '');
    setLugarAlmuerzo(data.lugar_almuerzo || '');
    setLugarAlmuerzoOtro(data.lugar_almuerzo_otro || '');
    const hasPersonal = (data.personal || []).length > 0;
    setMode(data.estado === 'cerrado' ? 'asignacion' : hasPersonal ? 'listado' : 'listado');
  };

  const handleLoadExisting = async (p) => {
    try {
      const res = await rrhhApi.getParte(p.id);
      loadParte(res.data);
    } catch {
      toast({ title: 'Error al cargar parte', variant: 'destructive' });
    }
  };

  const buildPayload = () => ({
    ot_principal: otPrincipal,
    supervisor_nombre: supervisorNombre,
    prevencionista_nombre: prevencionistaNombre,
    nota_almuerzo_extra: notaAlmuerzoExtra,
    lugar_almuerzo: lugarAlmuerzo,
    lugar_almuerzo_otro: lugarAlmuerzoOtro,
    personal: personal.map(p => {
      const totalHoras = p.ausencia ? 0 :
        (calcHours(p.hora_entrada, p.hora_salida) + calcHours(p.hora_entrada_2, p.hora_salida_2));
      return {
        trabajador_numero: p.trabajador_numero,
        trabajador_nombre: p.trabajador_nombre || '',
        hora_entrada: p.hora_entrada || '08:00',
        hora_salida: p.hora_salida || '13:00',
        hora_entrada_2: p.hora_entrada_2 || '14:00',
        hora_salida_2: p.hora_salida_2 || '18:00',
        almuerzo: !!p.almuerzo,
        tipo_almuerzo: p.tipo_almuerzo || '',
        ausencia: p.ausencia || '',
        observaciones: p.observaciones || '',
        total_horas: Math.round(totalHoras * 100) / 100,
        asignaciones: (p.asignaciones || []).filter(a => (parseFloat(a.horas) || 0) > 0).map(a => ({
          ot_numero: a.ot_numero, horas: parseFloat(a.horas) || 0,
        })),
      };
    }),
  });

  const showConflictToast = (err, _fallback = 'Error') => {
    const det = err?.response?.data?.detail;
    if (err?.response?.status === 409 && det && Array.isArray(det.conflictos)) {
      const lines = det.conflictos.slice(0, 4).map(c => {
        const lugar = c.otro_lugar_almuerzo_otro || c.otro_lugar_almuerzo || '';
        const ot = `OT ${c.otro_ot_principal}${c.otro_ot_descripcion ? ' - ' + c.otro_ot_descripcion.substring(0, 25) : ''}`;
        return `${c.trabajador_nombre}: ${ot}${lugar ? ' (' + lugar + ')' : ''}`;
      });
      const extra = det.conflictos.length > 4 ? ` · +${det.conflictos.length - 4} más` : '';
      toast({
        title: det.message || 'Conflicto de almuerzos',
        description: lines.join(' · ') + extra,
        variant: 'destructive',
      });
      return true;
    }
    return false;
  };

  const handleSave = async (silent = false) => {
    if (!parte || parte.estado === 'cerrado') return;
    // Cancelar cualquier save pendiente (evita que un timer viejo reescriba lo que acabamos de guardar)
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    try {
      if (!silent) setSaving(true);
      await rrhhApi.updateParte(parte.id, buildPayload());
      if (!silent) {
        toast({ title: 'Parte guardado' });
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2500);
      }
    } catch (err) {
      const isConflict = showConflictToast(err);
      if (!isConflict && !silent) {
        toast({ title: 'Error al guardar', variant: 'destructive' });
      }
      throw err; // propaga para que flujos encadenados (enviar almuerzo) aborten
    } finally {
      if (!silent) setSaving(false);
    }
  };
  // Mantener ref actualizada para que scheduleSave use la versión fresca.
  handleSaveRef.current = handleSave;

  // Detecta trabajadores con problemas para cierre (saldo ≠ 0, OT vacía, sin total).
  // Retorna array de strings listo para mostrar en toast/banner.
  const getErroresCierre = useCallback(() => {
    if (!personal || personal.length === 0) return ['Agrega al menos un trabajador'];
    const errores = [];
    if (!supervisorNombre || !supervisorNombre.trim()) {
      errores.push('Debes asignar un Supervisor (responsable de dar VB)');
    }
    const presentes = personal.filter(p => !p.ausencia);
    if (presentes.length === 0) return ['Todos los trabajadores están marcados como ausentes'];
    for (const p of presentes) {
      const nombre = p.trabajador_nombre || `#${p.trabajador_numero}`;
      const total = getTotal(p);
      if (total <= 0) { errores.push(`${nombre}: sin total de horas`); continue; }
      const asignaciones = p.asignaciones || [];
      const assigned = getAssigned(p);
      const otVacias = asignaciones.filter(a => (parseFloat(a.horas) || 0) > 0 && !a.ot_numero).length;
      const diff = parseFloat((total - assigned).toFixed(2));
      if (Math.abs(diff) > 0.01) {
        errores.push(diff > 0
          ? `${nombre}: faltan ${diff}h por asignar`
          : `${nombre}: asignó ${-diff}h de más`);
      }
      if (otVacias > 0) {
        errores.push(`${nombre}: ${otVacias} asignación${otVacias > 1 ? 'es' : ''} sin OT`);
      }
      const otsUsadas = asignaciones.filter(a => (parseFloat(a.horas) || 0) > 0 && a.ot_numero).map(a => a.ot_numero);
      const otsRepetidas = [...new Set(otsUsadas.filter((ot, i) => otsUsadas.indexOf(ot) !== i))];
      if (otsRepetidas.length > 0) {
        errores.push(`${nombre}: OT ${otsRepetidas.join(', ')} asignada más de una vez — consolida las horas en una sola línea`);
      }
    }
    return errores;
  }, [personal, supervisorNombre]);

  // Estado del modal bloqueante de validación
  const [erroresDialog, setErroresDialog] = useState(null); // null o lista de strings

  // Devuelve true si la fila p tiene problemas (para destacar visualmente)
  const filaIncompleta = (p) => {
    if (p.ausencia) return false;
    const total = getTotal(p);
    if (total <= 0) return true;
    const assigned = getAssigned(p);
    if (Math.abs(total - assigned) > 0.01) return true;
    const otVacias = (p.asignaciones || []).filter(a => (parseFloat(a.horas) || 0) > 0 && !a.ot_numero).length;
    if (otVacias > 0) return true;
    const ots = (p.asignaciones || []).filter(a => (parseFloat(a.horas) || 0) > 0 && a.ot_numero).map(a => a.ot_numero);
    return new Set(ots).size !== ots.length;
  };

  const handleClose = async () => {
    if (!parte || closing) return;
    const errores = getErroresCierre();
    if (errores.length > 0) {
      // Modal bloqueante con la lista completa
      setErroresDialog(errores);
      return;
    }
    // Deshabilitar el botón ANTES del guardado para evitar doble clic
    setClosing(true);
    try {
      await handleSave(true);
    } catch {
      setClosing(false);
      return;
    }
    try {
      const res = await rrhhApi.cerrarParte(parte.id);
      toast({ title: res.data.message });
      setParte(prev => ({ ...prev, estado: 'cerrado', hh_generados: res.data.hh_count }));
      setClosedFlash(true);
      setTimeout(() => setClosedFlash(false), 2500);
    } catch (err) {
      const det = err.response?.data?.detail;
      const msg = typeof det === 'object' ? det.message || 'Error al cerrar' : det || 'Error al cerrar';
      const sub = typeof det === 'object' && Array.isArray(det.errores) ? det.errores.slice(0, 5).join(' · ') : '';
      toast({ title: msg, description: sub, variant: 'destructive' });
    } finally {
      setClosing(false);
    }
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      const res = await rrhhApi.reabrirParte(parte.id);
      toast({ title: res.data.message });
      setParte(prev => ({ ...prev, estado: 'abierto' }));
      setMode('asignacion');
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setReopening(false);
    }
  };

  const handleSendAlmuerzo = async () => {
    if (!lugarAlmuerzo) {
      setLugarFlash(true);
      setTimeout(() => setLugarFlash(false), 3000);
      toast({ title: 'Debe seleccionar el lugar de almuerzo antes de enviar', variant: 'destructive' });
      return;
    }
    if (lugarAlmuerzo === 'otro' && !lugarAlmuerzoOtro.trim()) {
      setLugarFlash(true);
      setTimeout(() => setLugarFlash(false), 3000);
      toast({ title: 'Debe indicar el lugar en el campo "Otro"', variant: 'destructive' });
      return;
    }
    // Save first — si hay conflicto de almuerzo, handleSave lanza y abortamos
    try {
      await handleSave(true);
    } catch {
      return;
    }
    const presentes = personal.filter(p => !p.ausencia);
    const count = presentes.filter(p => p.almuerzo).length;
    const solicitadoPor = user?.nombre || user?.email || '';
    setSendingEmail(true);
    try {
      const res = await rrhhApi.enviarAlmuerzo({
        parte_id: parte.id,
        fecha: parte.fecha,
        ot_principal: parte.ot_principal || otPrincipal,
        ot_descripcion: parte.ot_descripcion || '',
        personal: personal.map(p => ({
          nombre: p.trabajador_nombre || '',
          almuerzo: !!p.almuerzo,
          tipo_almuerzo: p.tipo_almuerzo || 'A',
          ausencia: p.ausencia || '',
        })),
        nota_extra: notaAlmuerzoExtra,
        supervisor_nombre: supervisorNombre,
        lugar_almuerzo: lugarAlmuerzo,
        lugar_almuerzo_texto: (() => {
          if (lugarAlmuerzo === 'casino_cliente') return `Casino ${parte.cliente_nombre || 'Cliente'}`;
          if (lugarAlmuerzo === 'otro') return lugarAlmuerzoOtro || 'Otro';
          // Nuevo: resolver nombre del concesionario dinámico
          const c = concesionariosActivos.find(c => c.lugar === lugarAlmuerzo);
          if (c) return c.nombre || c.lugar;
          // Compat: keys legacy hardcoded (por si quedan partes antiguos en memoria)
          if (lugarAlmuerzo === 'sitio_escuadron') return 'Sitio Escuadrón';
          if (lugarAlmuerzo === 'maestranza') return 'Maestranza';
          return lugarAlmuerzo || '';
        })(),
        solicitado_por: solicitadoPor,
      });
      toast({ title: `${count} almuerzos solicitados`, description: res.data.message });
    } catch (err) {
      if (!showConflictToast(err)) {
        const det = err.response?.data?.detail;
        const msg = typeof det === 'object' ? det.message || 'Error al enviar' : det || 'Error al enviar';
        toast({ title: msg, variant: 'destructive' });
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const OTS_PARAGUAS = [999, 992];
  const esParaguas = parte?.es_paraguas ?? OTS_PARAGUAS.includes(parte?.ot_principal || otPrincipal);
  const defaultAsigOT = () => (esParaguas ? 0 : (otPrincipal || 0));

  // ---- Worker management ----
  const addWorker = (workerNum, opt) => {
    if (!opt) return;
    const w = opt.raw || allWorkers.find(w => w.numero === workerNum);
    if (!w) return;
    if (personal.find(p => p.trabajador_numero === w.numero)) {
      toast({ title: 'Ya esta en el listado' }); return;
    }
    setPersonal(prev => [...prev, {
      trabajador_numero: w.numero, trabajador_nombre: w.nombre,
      hora_entrada: '', hora_salida: '',
      hora_entrada_2: '', hora_salida_2: '',
      almuerzo: false, tipo_almuerzo: '', ausencia: '', observaciones: '',
      asignaciones: [{ _uid: _newUid(), ot_numero: defaultAsigOT(), horas: 0 }],
    }]);
    scheduleSave();
  };

  const removeWorker = (idx) => { setPersonal(prev => prev.filter((_, i) => i !== idx)); scheduleSave(); };

  const loadPreviousDayPersonal = async () => {
    if (!parte?.id) return;
    try {
      const res = await rrhhApi.getPersonalAnterior(parte.id);
      const prevPersonal = res.data.personal || [];
      if (prevPersonal.length === 0) {
        toast({ title: 'No se encontró personal del día anterior' });
        return;
      }
      // Add workers not already in the list
      const currentNums = new Set(personal.map(p => p.trabajador_numero));
      let added = 0;
      const toAdd = [];
      for (const p of prevPersonal) {
        if (!currentNums.has(p.trabajador_numero)) {
          toAdd.push({
            trabajador_numero: p.trabajador_numero,
            trabajador_nombre: p.trabajador_nombre || '',
            hora_entrada: '', hora_salida: '',
            hora_entrada_2: '', hora_salida_2: '',
            almuerzo: false, tipo_almuerzo: '', ausencia: '', observaciones: '',
            asignaciones: [{ _uid: _newUid(), ot_numero: defaultAsigOT(), horas: 0 }],
          });
          added++;
        }
      }
      if (toAdd.length > 0) {
        setPersonal(prev => [...prev, ...toAdd]);
        scheduleSave();
      }
      toast({ title: `${added} trabajadores agregados del ${res.data.fecha_anterior} (${prevPersonal.length} total)` });
    } catch {
      toast({ title: 'Error al cargar personal anterior', variant: 'destructive' });
    }
  };

  const updateField = (idx, field, value) => {
    setPersonal(prev => {
      const updated = [...prev];
      const w = { ...updated[idx], [field]: value };
      if (['hora_entrada', 'hora_salida', 'hora_entrada_2', 'hora_salida_2'].includes(field)) {
        const newTotal = calcHours(w.hora_entrada, w.hora_salida) + calcHours(w.hora_entrada_2, w.hora_salida_2);
        if ((w.asignaciones || []).length === 1) {
          // Redondear a 2 decimales (no a 0.5 ni a 0.1) para soportar cualquier minuto: 18:10 → 9.17h
          w.asignaciones = [{ ...w.asignaciones[0], horas: Math.round(newTotal * 100) / 100 }];
        }
      }
      updated[idx] = w;
      return updated;
    });
    scheduleSave();
  };

  const toggleAllAlmuerzo = async () => {
    const allSelected = personal.every(p => p.almuerzo);
    // Si estamos activando masivamente, verifica conflictos cruzados
    if (!allSelected && parte?.fecha) {
      try {
        const candidatos = personal.filter(p => !p.almuerzo && !p.ausencia);
        const checks = await Promise.all(
          candidatos.map(p =>
            rrhhApi.checkAlmuerzo({
              fecha: parte.fecha,
              trabajador_numero: p.trabajador_numero,
              except_parte_id: parte.id,
            }).then(r => ({ p, cfs: r.data?.conflictos || [] })).catch(() => ({ p, cfs: [] }))
          )
        );
        const conflictivos = checks.filter(x => x.cfs.length > 0);
        if (conflictivos.length > 0) {
          const nombres = conflictivos.slice(0, 5).map(x => x.p.trabajador_nombre).join(', ');
          const extra = conflictivos.length > 5 ? ` (+${conflictivos.length - 5} más)` : '';
          toast({
            title: `${conflictivos.length} trabajador(es) ya tienen almuerzo en otro parte`,
            description: `No se marcaron: ${nombres}${extra}`,
            variant: 'destructive',
          });
          const conflictIds = new Set(conflictivos.map(x => x.p.trabajador_numero));
          setPersonal(prev => prev.map(p => {
            if (conflictIds.has(p.trabajador_numero)) return p;
            return { ...p, almuerzo: true, tipo_almuerzo: p.tipo_almuerzo || 'A' };
          }));
          scheduleSave();
          return;
        }
      } catch (e) {
        console.warn('[ParteDiario] pre-check almuerzo falló, confiando en save final:', e);
      }
    }
    setPersonal(prev => prev.map(p => ({ ...p, almuerzo: !allSelected, tipo_almuerzo: !allSelected ? (p.tipo_almuerzo || 'A') : '' })));
    scheduleSave();
  };

  // ---- OT Assignment with auto-saldo ----
  const updateOTNum = (wi, oi, v) => {
    setPersonal(prev => { const u = [...prev]; const w = { ...u[wi] }; w.asignaciones = [...(w.asignaciones || [])]; w.asignaciones[oi] = { ...w.asignaciones[oi], ot_numero: v || 0 }; u[wi] = w; return u; });
    scheduleSave();
  };

  const updateOTHoras = (wi, oi, val) => {
    setPersonal(prev => {
      const u = [...prev]; const w = { ...u[wi] };
      const total = calcHours(w.hora_entrada, w.hora_salida) + calcHours(w.hora_entrada_2, w.hora_salida_2);
      const asigs = [...(w.asignaciones || [])];
      asigs[oi] = { ...asigs[oi], horas: parseFloat(val) || 0 };
      const sum = asigs.reduce((s, a) => s + (parseFloat(a.horas) || 0), 0);
      const saldo = Math.round((total - sum) * 100) / 100;
      if (saldo > 0.01 && oi === asigs.length - 1) asigs.push({ _uid: _newUid(), ot_numero: 0, horas: saldo });
      if (saldo <= 0.01 && asigs.length > oi + 1 && (parseFloat(asigs[oi + 1].horas) || 0) <= 0.01 && !asigs[oi + 1].ot_numero) asigs.splice(oi + 1, 1);
      w.asignaciones = asigs; u[wi] = w; return u;
    });
    scheduleSave();
  };

  const removeOTAsig = (wi, oi) => {
    setPersonal(prev => {
      const u = [...prev]; const w = { ...u[wi] };
      w.asignaciones = w.asignaciones.filter((_, i) => i !== oi);
      if (w.asignaciones.length === 0) {
        const total = calcHours(w.hora_entrada, w.hora_salida) + calcHours(w.hora_entrada_2, w.hora_salida_2);
        w.asignaciones = [{ _uid: _newUid(), ot_numero: defaultAsigOT(), horas: Math.round(total * 100) / 100 }];
      }
      u[wi] = w; return u;
    });
    scheduleSave();
  };

  const getTotal = (w) => calcHours(w.hora_entrada, w.hora_salida) + calcHours(w.hora_entrada_2, w.hora_salida_2);
  const getAssigned = (w) => (w.asignaciones || []).reduce((s, a) => s + (parseFloat(a.horas) || 0), 0);

  // Determine if the current fecha is a Friday
  const isFriday = (() => {
    if (!fecha) return false;
    try {
      const [dd, mm, yyyy] = fecha.split('/');
      const d = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
      return d.getDay() === 5;
    } catch { return false; }
  })();

  // Get configured schedule
  const getSchedule = () => {
    const hc = horarioConfig || {};
    const entrada = hc.horario_lv_entrada || '08:00';
    const salidaAm = hc.horario_lv_salida_am || '13:00';
    const entradaPm = hc.horario_lv_entrada_pm || '14:00';
    const salidaPm = isFriday ? (hc.horario_vi_salida || '17:00') : (hc.horario_lv_salida || '18:00');
    return { entrada, salidaAm, entradaPm, salidaPm };
  };

  // Fill schedule for next worker without schedule (one at a time)
  const asignarHHUnoAUno = () => {
    const sched = getSchedule();
    let filledIdx = -1;
    setPersonal(prev => {
      const updated = [...prev];
      for (let i = 0; i < updated.length; i++) {
        const w = updated[i];
        if (w.ausencia) continue;
        // Check if already has schedule filled
        if (w.hora_entrada && w.hora_salida && w.hora_entrada_2 && w.hora_salida_2) continue;
        // Fill this one
        const newW = { ...w,
          hora_entrada: sched.entrada,
          hora_salida: sched.salidaAm,
          hora_entrada_2: sched.entradaPm,
          hora_salida_2: sched.salidaPm,
        };
        const total = calcHours(newW.hora_entrada, newW.hora_salida) + calcHours(newW.hora_entrada_2, newW.hora_salida_2);
        newW.asignaciones = [{ _uid: _newUid(), ot_numero: defaultAsigOT(), horas: Math.round(total * 100) / 100 }];
        updated[i] = newW;
        filledIdx = i;
        // Count how many remain after this one
        let remaining = 0;
        for (let j = i + 1; j < updated.length; j++) {
          if (!updated[j].ausencia && !(updated[j].hora_entrada && updated[j].hora_salida && updated[j].hora_entrada_2 && updated[j].hora_salida_2)) remaining++;
        }
        toast({ title: `Horario cargado: ${w.trabajador_nombre}${isFriday ? ' (Vi)' : ''}`, description: remaining > 0 ? `${remaining} trabajador${remaining > 1 ? 'es' : ''} restante${remaining > 1 ? 's' : ''}` : 'Ultimo trabajador completado' });
        return updated;
      }
      toast({ title: 'Todos los trabajadores ya tienen horario asignado' });
      return prev;
    });
    if (filledIdx >= 0) {
      setLastFilledIdx(filledIdx);
      setTimeout(() => {
        const row = document.querySelector(`[data-testid="asig-row-${filledIdx}"]`);
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setLastFilledIdx(null), 3000);
    }
    scheduleSave();
  };

  // Count workers pending schedule fill
  const pendingScheduleCount = personal.filter(p => !p.ausencia && !(p.hora_entrada && p.hora_salida && p.hora_entrada_2 && p.hora_salida_2)).length;

  const isClosed = parte?.estado === 'cerrado';
  const personalNums = new Set(personal.map(p => p.trabajador_numero));
  const addWorkerOptions = workerOptions.filter(o => !personalNums.has(o.value));
  const totalAlmuerzos = personal.filter(p => p.almuerzo && !p.ausencia).length;
  const totalHH = personal.filter(p => !p.ausencia).reduce((s, p) => s + getTotal(p), 0);
  const totalAusentes = personal.filter(p => p.ausencia).length;
  const otLabel = otOptions.find(o => o.value === (parte?.ot_principal || otPrincipal))?.label || `OT ${parte?.ot_principal || otPrincipal}`;

  if (loading) return <FormSkeleton fields={6} />;

  // ========================= SETUP =========================
  if (!parte) {
    return (
      <div className="space-y-4 page-enter" data-testid="parte-diario-terreno">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="rounded-xl gap-1.5 text-xs mt-1"
            data-testid="btn-volver-parte-setup"
            title="Volver a pantalla anterior"
          >
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold tracking-tight">Parte Diario Terreno</h1>
            <p className="text-sm text-muted-foreground">Crear parte de asistencia y asignacion de HH</p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HardHat className="h-4 w-4" strokeWidth={1.5} /> Crear Parte Diario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Fecha</Label>
                <div className="flex gap-2">
                  <Input value={fecha} onChange={(e) => setFecha(e.target.value)} placeholder="DD/MM/AAAA" className="rounded-xl flex-1" data-testid="input-fecha-parte" />
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-xl shrink-0" data-testid="btn-calendar">
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={parseDate(fecha)}
                        onSelect={(d) => { if (d) { setFecha(formatDate(d)); setCalOpen(false); } }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">OT Principal</Label>
                <SearchableSelect
                  value={otPrincipal}
                  onChange={(v) => setOtPrincipal(v || 0)}
                  options={otOptions.filter(o => o.value !== 0)}
                  placeholder="Buscar OT por numero o descripcion..."
                  data-testid="select-ot-principal"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleCreateParte} disabled={saving || !otPrincipal} className="rounded-xl gap-1.5 w-full" data-testid="btn-crear-parte">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                  Crear Parte
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Buscar Partes Existentes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Buscar Parte Existente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Fecha</Label>
                <div className="flex gap-1">
                  <Input value={searchDate} onChange={(e) => setSearchDate(e.target.value)} placeholder="DD/MM/AAAA" className="rounded-xl w-36" data-testid="search-fecha" />
                  <Popover open={searchCalOpen} onOpenChange={setSearchCalOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-xl shrink-0 h-9 w-9">
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={searchDate ? parseDate(searchDate) : undefined} onSelect={(d) => { if (d) { setSearchDate(formatDate(d)); setSearchCalOpen(false); } }} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-1.5 w-64">
                <Label className="text-xs">OT</Label>
                <SearchableSelect
                  value={searchOT ? parseInt(searchOT) : null}
                  onChange={(v) => setSearchOT(v ? String(v) : '')}
                  options={otOptions.filter(o => o.value !== 0)}
                  placeholder="Filtrar por OT..."
                  data-testid="search-ot"
                />
              </div>
              <Button onClick={handleSearchPartes} disabled={searching} variant="outline" className="rounded-xl gap-1.5" data-testid="btn-buscar-partes">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarIcon className="h-4 w-4" />} Buscar
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {searchResults.map(p => (
                  <button key={p.id} onClick={() => handleLoadExisting(p)} className="flex items-center gap-3 p-3 rounded-xl border text-left hover:bg-muted/50 transition-colors" data-testid={`parte-result-${p.id}`}>
                    <div className={`p-1.5 rounded-lg ${p.estado === 'cerrado' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                      {p.estado === 'cerrado' ? <Lock className="h-3.5 w-3.5 text-emerald-600" /> : <Unlock className="h-3.5 w-3.5 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">OT {p.ot_principal} {p.ot_descripcion ? `- ${p.ot_descripcion.substring(0, 30)}` : ''}</p>
                      <p className="text-xs text-muted-foreground">{p.fecha} - {(p.personal || []).length} personas</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========================= ACTIVE PARTE =========================
  return (
    <div className="space-y-4 page-enter" data-testid="parte-diario-terreno">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="rounded-xl gap-1.5 text-xs mt-1"
          data-testid="btn-volver-parte"
          title="Volver a pantalla anterior"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Parte Diario Terreno</h1>
          <p className="text-sm text-muted-foreground">Planilla de asistencia y asignacion de HH</p>
        </div>
      </div>

      {/* Status banner: progreso del parte */}
      {parte && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="parte-status-banner">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium border ${
            parte.almuerzo_enviado
              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200'
          }`}>
            {parte.almuerzo_enviado ? <CheckCircle className="h-3.5 w-3.5" /> : <UtensilsCrossed className="h-3.5 w-3.5" />}
            Almuerzo: {parte.almuerzo_enviado
              ? `✓ Solicitado${parte.almuerzo_enviado_at ? ' ' + new Date(parte.almuerzo_enviado_at).toLocaleString('es-CL', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'}) : ''}`
              : 'Pendiente de enviar'}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium border ${
            isClosed
              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200'
              : 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-200'
          }`}>
            {isClosed ? <Lock className="h-3.5 w-3.5" /> : <PenLine className="h-3.5 w-3.5" />}
            OT y Horario: {isClosed
              ? `✓ Cerrado · ${parte.hh_generados || 0} HH`
              : 'Borrador abierto'}
          </span>
        </div>
      )}

      {/* Header */}
      <Card className={isClosed ? 'border-emerald-500/30' : 'border-primary/30'}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              {isClosed ? <Lock className="h-4 w-4 text-emerald-600" /> : <Unlock className="h-4 w-4 text-primary" />}
              <span className="font-semibold">{otLabel}</span>
              {esParaguas && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-400/30"
                  title="OT Paraguas: cada trabajador debe imputar sus horas a una OT real específica"
                  data-testid="badge-ot-paraguas"
                >
                  PARAGUAS
                </span>
              )}
              <span className="text-sm text-muted-foreground">| {parte.fecha}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isClosed ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : mode === 'listado' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'}`}>
                {isClosed ? 'Cerrado' : mode === 'listado' ? 'Asistencia' : 'Asignacion HH'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 rounded-lg bg-primary/10 text-primary font-medium">{personal.length - totalAusentes} presentes</span>
              {totalAusentes > 0 && <span className="px-2 py-1 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 font-medium">{totalAusentes} ausentes</span>}
              {mode === 'asignacion' && <span className="px-2 py-1 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">{totalHH.toFixed(1)} HH</span>}
              <span className="px-2 py-1 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                <UtensilsCrossed className="h-3 w-3" /> {totalAlmuerzos}
              </span>
            </div>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => { setParte(null); setPersonal([]); setMode('listado'); }} className="rounded-xl text-xs">Cambiar</Button>
          </div>
        </CardContent>
      </Card>

      {/* ============ LISTADO ============ */}
      {mode === 'listado' && !isClosed && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Listado de Asistencia</CardTitle>
                <Button variant="outline" size="sm" onClick={toggleAllAlmuerzo} className="rounded-xl gap-1.5 text-xs" data-testid="btn-toggle-almuerzo">
                  <CheckSquare className="h-3.5 w-3.5" />
                  {personal.every(p => p.almuerzo) ? 'Desmarcar Todos' : 'Marcar Todos'} Almuerzo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Trabajador</TableHead>
                    <TableHead className="w-36">Tipo Almuerzo</TableHead>
                    <TableHead className="w-36">Ausencia</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {personal.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Sin trabajadores. Agregue personal al listado.</TableCell></TableRow>
                  ) : personal.map((p, idx) => (
                    <TableRow key={`${p.trabajador_numero}-${idx}`} className={`hover:bg-muted/30 transition-colors ${p.ausencia ? 'opacity-60' : ''}`} data-testid={`listado-row-${idx}`}>
                      <TableCell className="text-xs text-muted-foreground tabular-nums">{idx + 1}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{p.trabajador_nombre}</p>
                        <p className="text-xs text-muted-foreground">N {p.trabajador_numero}</p>
                      </TableCell>
                      <TableCell className="p-1.5">
                        <select
                          className="h-8 w-full rounded-lg border bg-background px-2 text-xs"
                          value={p.almuerzo ? (p.tipo_almuerzo || 'A') : ''}
                          onChange={async (e) => {
                            const v = e.target.value;
                            if (!v) {
                              updateField(idx, 'almuerzo', false);
                              updateField(idx, 'tipo_almuerzo', '');
                              return;
                            }
                            // Verifica conflicto contra otros partes del mismo día
                            try {
                              const chk = await rrhhApi.checkAlmuerzo({
                                fecha: parte.fecha,
                                trabajador_numero: p.trabajador_numero,
                                except_parte_id: parte.id,
                              });
                              const cfs = chk.data?.conflictos || [];
                              if (cfs.length > 0) {
                                const c = cfs[0];
                                const lugar = c.lugar_almuerzo_otro || c.lugar_almuerzo || 'otro lugar';
                                toast({
                                  title: `${p.trabajador_nombre} ya tiene almuerzo ese día`,
                                  description: `Ya pedido en OT ${c.ot_principal}${c.ot_descripcion ? ' - ' + c.ot_descripcion : ''} (${lugar}). Un trabajador solo puede pedir almuerzo en UN parte por día.`,
                                  variant: 'destructive',
                                });
                                return; // NO aplica el cambio
                              }
                            } catch (e) {
                              console.warn('[ParteDiario] check conflicto almuerzo falló:', e);
                            }
                            updateField(idx, 'almuerzo', true);
                            updateField(idx, 'tipo_almuerzo', v);
                          }}
                          disabled={!!p.ausencia}
                          data-testid={`select-almuerzo-${idx}`}
                        >
                          <option value="">No</option>
                          <option value="A">A - Normal</option>
                          <option value="H">H - Hipocalórico</option>
                          <option value="C">C - Colación</option>
                        </select>
                      </TableCell>
                      <TableCell className="p-1.5">
                        <select
                          className={`h-8 w-full rounded-lg border bg-background px-2 text-xs ${AUSENCIA_OPTIONS.find(a => a.value === (p.ausencia || ''))?.color || ''}`}
                          value={p.ausencia || ''}
                          onChange={(e) => updateField(idx, 'ausencia', e.target.value)}
                          data-testid={`select-ausencia-${idx}`}
                        >
                          {AUSENCIA_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => removeWorker(idx)} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive" data-testid={`btn-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <div className="w-72">
              <SearchableSelect value={null} onChange={addWorker} options={addWorkerOptions} placeholder="Agregar trabajador (nombre o numero)..." data-testid="search-add-worker" />
            </div>
            {!isClosed && (
              <Button variant="outline" size="sm" className="rounded-xl gap-1.5 text-xs" onClick={loadPreviousDayPersonal}
                data-testid="btn-cargar-anterior">
                <Users className="h-3.5 w-3.5" /> Cargar personal día anterior
              </Button>
            )}
            <div className="flex-1" />
            {!isClosed && pendingScheduleCount > 0 && (
              <Button variant="outline" size="sm" onClick={asignarHHUnoAUno} disabled={personal.length === 0} className="rounded-xl gap-1.5 text-xs border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                data-testid="btn-asignar-hh-auto">
                <Clock className="h-3.5 w-3.5 text-emerald-600" /> Llenar Horario ({pendingScheduleCount})
              </Button>
            )}
            {!isClosed && (
              <Button variant="outline" size="sm" onClick={handleSendAlmuerzo} disabled={sendingEmail || totalAlmuerzos === 0 || parte?.almuerzo_enviado}
                className={`rounded-xl gap-1.5 text-xs ${parte?.almuerzo_enviado ? 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : ''}`}
                data-testid="btn-enviar-almuerzo-top"
                title={parte?.almuerzo_enviado ? `Ya enviado${parte.almuerzo_enviado_at ? ' el ' + new Date(parte.almuerzo_enviado_at).toLocaleString('es-CL') : ''}` : ''}>
                {sendingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                 parte?.almuerzo_enviado ? <CheckCircle className="h-3.5 w-3.5" /> :
                 <Mail className="h-3.5 w-3.5" />}
                {parte?.almuerzo_enviado ? `✓ Almuerzo Enviado (${totalAlmuerzos})` : `Enviar Almuerzo (${totalAlmuerzos})`}
              </Button>
            )}
            <Button onClick={() => { handleSave(true); setMode('asignacion'); }} disabled={personal.length === 0}
              className={`rounded-xl gap-1.5 ${isClosed ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              data-testid="btn-asignar-hh">
              <Clock className="h-4 w-4" />
              {isClosed ? `✓ OT y Horario Asignados (${parte?.hh_generados || 0} HH)` : 'Asignar OT y Horario'}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Lugar + Nota almuerzo + Enviar */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5"><UtensilsCrossed className="h-3 w-3" /> Lugar de almuerzo</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className={`h-9 rounded-xl border bg-background px-3 text-sm flex-1 max-w-xs transition-all ${lugarFlash ? 'animate-pulse ring-2 ring-red-500 border-red-500' : ''}`}
                      value={lugarAlmuerzo}
                      onChange={(e) => { setLugarAlmuerzo(e.target.value); setLugarFlash(false); scheduleSave(); }}
                      data-testid="select-lugar-almuerzo"
                    >
                      <option value="">Seleccionar lugar...</option>
                      <option value="casino_cliente">Casino Cliente{parte?.cliente_nombre ? ` (${parte.cliente_nombre})` : ''}</option>
                      {concesionariosActivos.map((c) => (
                        <option key={c.lugar} value={c.lugar}>{c.nombre || c.lugar}</option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {lugarAlmuerzo === 'otro' && (
                      <Input value={lugarAlmuerzoOtro} onChange={(e) => { setLugarAlmuerzoOtro(e.target.value); scheduleSave(); }} placeholder="Indicar lugar..." className="rounded-xl flex-1 max-w-xs" data-testid="input-lugar-otro" />
                    )}
                  </div>
                  {/* Iter 236.v — badge readonly del proveedor Alegra vinculado */}
                  {(() => {
                    const cSel = concesionariosActivos.find(c => c.lugar === lugarAlmuerzo);
                    if (!cSel?.proveedor_rut) return null;
                    return (
                      <div className="flex items-center gap-1.5 text-[11px] text-blue-700 dark:text-blue-300 mt-1"
                           data-testid="terreno-proveedor-badge">
                        <span className="font-mono bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded px-1.5 py-0.5">
                          Proveedor: {cSel.proveedor_rut}
                        </span>
                        <span className="text-muted-foreground">·</span>
                        <span className="truncate max-w-[280px]">{cSel.proveedor_razon_social || '—'}</span>
                        <span className="ml-1 text-[10px] text-muted-foreground/70">(auto vinculado, no editable)</span>
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nota almuerzo adicional (personas no en nomina)</Label>
                  <Input value={notaAlmuerzoExtra} onChange={(e) => { setNotaAlmuerzoExtra(e.target.value); scheduleSave(); }} placeholder="Ej: 2 almuerzos extra para visita tecnica..." className="rounded-xl" data-testid="input-nota-almuerzo" />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleSendAlmuerzo}
                    disabled={sendingEmail || personal.length === 0 || parte?.almuerzo_enviado}
                    variant="outline"
                    className={`rounded-xl gap-1.5 ${parte?.almuerzo_enviado ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : ''}`}
                    data-testid="btn-enviar-almuerzo"
                    title={parte?.almuerzo_enviado ? `Ya enviado${parte.almuerzo_enviado_at ? ' el ' + new Date(parte.almuerzo_enviado_at).toLocaleString('es-CL') : ''}` : ''}
                  >
                    {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> :
                     parte?.almuerzo_enviado ? <CheckCircle className="h-4 w-4" /> :
                     <Mail className="h-4 w-4" />}
                    {parte?.almuerzo_enviado
                      ? `✓ Almuerzo Enviado${parte.almuerzo_enviado_at ? ' — ' + new Date(parte.almuerzo_enviado_at).toLocaleString('es-CL', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'}) : ''}`
                      : `Enviar Almuerzo (${totalAlmuerzos})`}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ============ ASIGNACION HH ============ */}
      {(mode === 'asignacion' || isClosed) && (
        <>
          {!isClosed && (
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setMode('listado')} className="rounded-xl gap-1.5 text-xs" data-testid="btn-volver-asignacion">
                <ArrowLeft className="h-3.5 w-3.5" /> Volver a Asistencia
              </Button>
              <div className="flex-1" />
              {pendingScheduleCount > 0 && (
                <Button variant="outline" size="sm" onClick={asignarHHUnoAUno} className="rounded-xl gap-1.5 text-xs border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                  data-testid="btn-llenar-horario">
                  <Clock className="h-3.5 w-3.5 text-emerald-600" /> Llenar Horario ({pendingScheduleCount})
                </Button>
              )}
              {pendingScheduleCount === 0 && personal.length > 0 && (
                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5" /> Todos los horarios completos
                </span>
              )}
              <div className="w-72">
                <SearchableSelect value={null} onChange={addWorker} options={addWorkerOptions} placeholder="Agregar trabajador..." disabled={isClosed} data-testid="search-add-worker-asig" />
              </div>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="px-3 py-1.5 text-[11px] text-muted-foreground bg-muted/40 border-b flex items-center gap-2 flex-wrap" data-testid="banner-tope-hn">
                <Clock className="h-3 w-3" />
                <span>Tope HN del día (<b>{fecha}</b>): <b className="text-foreground">{tope > 0 ? `${tope}h` : 'sin jornada (todo HE)'}</b></span>
                <span className="text-muted-foreground/70">— Lo trabajado por encima del tope se clasifica como Hora Extra automáticamente.</span>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="h-8">
                      <TableHead className="w-8 text-[11px] py-1">#</TableHead>
                      <TableHead className="min-w-[140px] text-[11px] py-1">Trabajador</TableHead>
                      <TableHead className="w-20 text-[11px] py-1">Estado</TableHead>
                      <TableHead className="w-[68px] text-[11px] py-1">Entrada</TableHead>
                      <TableHead className="w-[68px] text-[11px] py-1">Salida</TableHead>
                      <TableHead className="w-[68px] text-[11px] py-1">Entrada 2</TableHead>
                      <TableHead className="w-[68px] text-[11px] py-1">Salida 2</TableHead>
                      <TableHead className="w-12 text-center text-[11px] py-1">Total</TableHead>
                      <TableHead className="w-10 text-center text-[11px] py-1" title={`Tope diario: ${tope}h`}>HN</TableHead>
                      <TableHead className="w-10 text-center text-[11px] py-1 text-amber-700">HE</TableHead>
                      <TableHead className="min-w-[260px] text-[11px] py-1">Asignacion OTs</TableHead>
                      <TableHead className="w-12 text-center text-[11px] py-1">Saldo</TableHead>
                      <TableHead className="min-w-[90px] text-[11px] py-1">Obs.</TableHead>
                      {!isClosed && <TableHead className="w-8 py-1" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {personal.map((p, idx) => {
                      const isAbsent = !!p.ausencia;
                      const total = isAbsent ? 0 : getTotal(p);
                      const assigned = isAbsent ? 0 : getAssigned(p);
                      const saldo = Math.round((total - assigned) * 100) / 100;
                      const ausLabel = AUSENCIA_OPTIONS.find(a => a.value === p.ausencia);
                      const incompleta = !isClosed && filaIncompleta(p);
                      return (
                        <TableRow key={`${p.trabajador_numero}-${idx}`} className={`hover:bg-muted/30 transition-all align-middle ${isAbsent ? 'opacity-50' : ''} ${incompleta ? 'bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-300 ring-inset' : ''} ${lastFilledIdx === idx && !incompleta ? 'bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-400/50 ring-inset' : ''}`} data-testid={`asig-row-${idx}`}>
                          <TableCell className="text-xs text-muted-foreground tabular-nums py-1">{idx + 1}</TableCell>
                          <TableCell className="py-1">
                            <p className="text-xs font-medium leading-tight">{p.trabajador_nombre}</p>
                            <p className="text-[10px] text-muted-foreground leading-tight">N {p.trabajador_numero}</p>
                          </TableCell>
                          <TableCell className="p-0.5">
                            <select
                              className={`h-6 w-full rounded border bg-background px-1 text-[11px] ${ausLabel?.color || ''}`}
                              value={p.ausencia || ''}
                              onChange={(e) => updateField(idx, 'ausencia', e.target.value)}
                              disabled={isClosed}
                              data-testid={`select-ausencia-asig-${idx}`}
                            >
                              {AUSENCIA_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                            </select>
                          </TableCell>
                          {['hora_entrada', 'hora_salida', 'hora_entrada_2', 'hora_salida_2'].map(field => (
                            <TableCell key={field} className="p-0.5">
                              <Input type="time" value={p[field] || ''} onChange={(e) => updateField(idx, field, e.target.value)} disabled={isClosed || isAbsent} className="rounded h-6 text-[11px] px-1 text-center" />
                            </TableCell>
                          ))}
                          <TableCell className="text-center font-bold tabular-nums text-xs py-1">{isAbsent ? '-' : total.toFixed(1)}</TableCell>
                          <TableCell className="text-center tabular-nums text-xs py-1" data-testid={`hn-cell-${idx}`}>
                            {isAbsent ? '-' : splitHnHe(total, tope).hn.toFixed(1)}
                          </TableCell>
                          <TableCell className="text-center tabular-nums text-xs py-1 text-amber-700 font-semibold" data-testid={`he-cell-${idx}`}>
                            {isAbsent ? '-' : (splitHnHe(total, tope).he > 0 ? splitHnHe(total, tope).he.toFixed(1) : '0')}
                          </TableCell>
                          <TableCell className="p-0.5">
                            {isAbsent ? (
                              <span className={`text-xs font-medium ${ausLabel?.color || ''}`}>{ausLabel?.label || p.ausencia}</span>
                            ) : (
                              <div className="space-y-0.5">
                                {(p.asignaciones || []).map((asig, oi) => (
                                  <div key={asig._uid || `legacy-${oi}`} className="flex items-center gap-1">
                                    <SearchableSelect value={asig.ot_numero} onChange={(v) => updateOTNum(idx, oi, v)} options={otOptions} placeholder="OT..." disabled={isClosed} className="flex-1" data-testid={`ot-select-${idx}-${oi}`} />
                                    <Input type="number" step="0.01" min="0" value={asig.horas} onChange={(e) => updateOTHoras(idx, oi, e.target.value)} disabled={isClosed} className="rounded h-6 w-14 text-[11px] text-center px-1" data-testid={`ot-horas-${idx}-${oi}`} />
                                    <span className="text-[10px] text-muted-foreground">h</span>
                                    {!isClosed && (p.asignaciones || []).length > 1 && (
                                      <button onClick={() => removeOTAsig(idx, oi)} className="text-muted-foreground hover:text-destructive p-0.5"><Trash2 className="h-3 w-3" /></button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-center py-1">
                            {isAbsent ? (
                              <span className="text-xs text-muted-foreground">-</span>
                            ) : (
                              <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${Math.abs(saldo) < 0.05 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : saldo > 0 ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
                                {saldo.toFixed(2)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="p-0.5">
                            <Input value={p.observaciones || ''} onChange={(e) => updateField(idx, 'observaciones', e.target.value)} disabled={isClosed} className="rounded h-6 text-[11px] px-1" placeholder="..." />
                          </TableCell>
                          {!isClosed && (
                            <TableCell className="p-0.5">
                              <Button variant="ghost" size="icon" onClick={() => removeWorker(idx)} className="h-6 w-6 rounded text-muted-foreground hover:text-destructive" data-testid={`btn-remove-asig-${idx}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Firmas */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3 text-sm font-medium text-muted-foreground">
                <PenLine className="h-4 w-4" /> Firmas
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Supervisor</Label>
                  <SearchableSelect
                    value={supervisorNombre}
                    displayValue={supervisorNombre}
                    onChange={(v, opt) => { setSupervisorNombre(opt ? opt.label : v || ''); scheduleSave(); }}
                    options={workerOptions}
                    placeholder="Buscar supervisor por nombre..."
                    disabled={isClosed}
                    data-testid="input-supervisor"
                  />
                  {/* PIN para firmar VB Supervisor (visible cuando parte cerrado) */}
                  {isClosed && (
                    <div className="pt-2">
                      <FirmaSupervisorInline parte={parte} onFirmado={(vb) => setParte(prev => ({ ...prev, vb_supervisor: vb }))} />
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Prevencionista</Label>
                  <SearchableSelect
                    value={prevencionistaNombre}
                    displayValue={prevencionistaNombre}
                    onChange={(v, opt) => { setPrevencionistaNombre(opt ? opt.label : v || ''); scheduleSave(); }}
                    options={workerOptions}
                    placeholder="Buscar prevencionista por nombre..."
                    disabled={isClosed}
                    data-testid="input-prevencionista"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {!isClosed ? (
              <>
                <Button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  variant="outline"
                  className={`rounded-xl gap-1.5 transition-all ${savedFlash ? 'bg-emerald-100 border-emerald-500 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : ''}`}
                  data-testid="btn-guardar-parte"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : savedFlash ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <Save className="h-4 w-4" />}
                  {savedFlash ? '✓ Grabado' : 'Guardar Borrador'}
                </Button>
                <div className="flex-1" />
                {(() => {
                  const errores = getErroresCierre();
                  const hayErrores = errores.length > 0;
                  return (
                    <div className="flex items-center gap-2">
                      {hayErrores && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-300 text-amber-900 dark:text-amber-200 text-xs"
                             title={errores.join('\n')}
                             data-testid="banner-errores-cierre">
                          <span className="font-semibold">{errores.length}</span>
                          <span>{errores.length === 1 ? 'registro' : 'registros'} incompletos</span>
                        </div>
                      )}
                      <Button
                        onClick={handleClose}
                        disabled={closing || hayErrores || personal.filter(p => !p.ausencia).length === 0}
                        className={`rounded-xl gap-1.5 text-white disabled:bg-slate-300 disabled:text-slate-500 transition-all ${
                          closedFlash
                            ? 'bg-emerald-700 hover:bg-emerald-800 ring-4 ring-emerald-300 animate-pulse'
                            : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                        data-testid="btn-cerrar-parte"
                        title={hayErrores ? errores.join('\n') : 'Cerrar el parte y generar registros HH'}
                      >
                        {closing ? <Loader2 className="h-4 w-4 animate-spin" /> :
                         closedFlash ? <CheckCircle className="h-4 w-4" /> :
                         <Lock className="h-4 w-4" />}
                        {closedFlash ? '✓ Grabado - HH Generadas' : 'Cerrar y Generar HH'}
                      </Button>
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                  <Lock className="h-4 w-4" /> Parte cerrado - {parte.hh_generados || 0} registros HH generados
                  {supervisorNombre && <span className="text-muted-foreground ml-2">| Sup: {supervisorNombre}</span>}
                  {prevencionistaNombre && <span className="text-muted-foreground">| Prev: {prevencionistaNombre}</span>}
                </div>
                <div className="flex-1" />
                <Button onClick={handleReopen} disabled={reopening} variant="outline" className="rounded-xl gap-1.5" data-testid="btn-reabrir-parte">
                  {reopening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />} Reabrir
                </Button>
              </>
            )}
          </div>
        </>
      )}

      {/* Modal bloqueante de errores al intentar cerrar */}
      <AlertDialog open={!!erroresDialog} onOpenChange={(o) => !o && setErroresDialog(null)}>
        <AlertDialogContent data-testid="dialog-errores-cierre">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-700 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" /> No se puede cerrar el parte
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <p className="text-sm">Hay <b>{erroresDialog?.length || 0}</b> registro(s) incompletos. Las filas con problema están marcadas en rosa.</p>
                <ul className="space-y-1 text-xs bg-muted/50 rounded p-2 max-h-72 overflow-y-auto list-disc list-inside">
                  {(erroresDialog || []).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
                <p className="text-[11px] text-muted-foreground">Sugerencias: completá horario y OT, o marcá al trabajador como ausente (F/L/P/V/etc.) para excluirlo del cálculo.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErroresDialog(null)} data-testid="btn-cerrar-errores">Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============= Componente firma PIN inline en parte cerrado =============
function FirmaSupervisorInline({ parte, onFirmado }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const dado = !!(parte?.vb_supervisor?.dado);
  const supNombre = parte?.supervisor_nombre;

  if (dado) {
    const vb = parte.vb_supervisor;
    return (
      <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 text-emerald-800 dark:text-emerald-200" data-testid="vb-firmado">
        <CheckCircle className="h-3.5 w-3.5" />
        Firmado · {vb?.supervisor_nombre || supNombre}
      </div>
    );
  }

  if (!supNombre) {
    return (
      <div className="text-[11px] text-amber-700 dark:text-amber-400 px-2 py-1 rounded bg-amber-50 border border-amber-200">
        Falta supervisor en el parte para poder firmar
      </div>
    );
  }

  const firmar = async () => {
    if (pin.length < 4) {
      toast({ title: 'PIN inválido', description: 'Mínimo 4 dígitos', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/partes-vb/${parte.id}/dar-vb`, { pin });
      toast({ title: '✓ Firmado', description: res.data?.vb_supervisor?.supervisor_nombre || supNombre });
      onFirmado(res.data.vb_supervisor);
      setPin('');
    } catch (err) {
      toast({ title: 'No se pudo firmar', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-muted/40 border" data-testid="firma-pin-inline">
      <span className="text-[11px] text-muted-foreground">PIN de <b>{supNombre}</b>:</span>
      <Input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={8}
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => e.key === 'Enter' && firmar()}
        placeholder="••••"
        className="h-7 w-20 text-center text-xs"
        disabled={busy}
        data-testid="input-pin-supervisor"
      />
      <Button size="sm" onClick={firmar} disabled={busy || pin.length < 4} className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="btn-firmar-pin">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />} Firmar
      </Button>
    </div>
  );
}
